import React, { useCallback, useEffect, useMemo, useState } from "react";
import UIkit from "uikit";

import StorageService from "../../../../services/storage.service";
import { formatCurrency } from "../data";
import StorageMenu from "../menu";

const SectionRow = ({ title, children }) => (
  <div className="uk-margin-large-bottom">
    <div className="uk-flex uk-flex-between uk-flex-middle">
      <h3 className="uk-margin-remove">{title}</h3>
    </div>
    {children}
  </div>
);

const MetricCard = ({ label, icon, value }) => (
  <div>
    <div className="rbr-card uk-margin-small-right">
      <div className="rbr-card-head">
        <div className="rbr-card-title">
          <span className="rbr-ico" uk-icon={icon}></span>
          {label}
        </div>
      </div>
      <div className="hms-blue-number">{value}</div>
    </div>
  </div>
);

export default function InventoryValueReport() {
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [drillDownItems, setDrillDownItems] = useState([]);

  const loadReport = useCallback(async () => {
    try {
      const resp = await StorageService.inventoryValueReport();
      setLocations(resp.data?.locations || []);
    } catch (err) {
      UIkit.notification("Failed to load inventory value report.", "error");
    }
  }, []);

  const loadLocation = useCallback(async (locationId) => {
    if (!locationId) {
      setDrillDownItems([]);
      return;
    }
    try {
      const resp = await StorageService.inventoryValueReport({ location_id: locationId });
      setDrillDownItems(resp.data?.locations?.[0]?.items || []);
    } catch (err) {
      UIkit.notification("Failed to load location inventory value.", "error");
    }
  }, []);

  useEffect(() => {
    document.title = "Inventory Value Report - RueBaRue";
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    loadLocation(selectedLocationId);
  }, [loadLocation, selectedLocationId]);

  const totals = useMemo(() => {
    return locations.reduce(
      (sum, row) => ({
        item_count: sum.item_count + Number(row.item_count || 0),
        inventory_value: sum.inventory_value + Number(row.inventory_value || 0),
        replacement_value: sum.replacement_value + Number(row.replacement_value || 0),
      }),
      { item_count: 0, inventory_value: 0, replacement_value: 0 },
    );
  }, [locations]);

  const selectedLocation = locations.find((row) => String(row.location_id) === String(selectedLocationId));

  return (
    <section id="dashboard-template">
      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <h1 className="uk-heading-small uk-margin-remove">Inventory Value Report</h1>
      </div>

      <div id="hms-main-body">
        <StorageMenu />
        <SectionRow title="Inventory Value">
          <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-3@m uk-margin-small-top" uk-grid="true">
            <MetricCard label="Item Count" icon="album" value={totals.item_count.toLocaleString()} />
            <MetricCard label="Inventory Value" icon="credit-card" value={formatCurrency(totals.inventory_value)} />
            <MetricCard label="Replacement Value" icon="refresh" value={formatCurrency(totals.replacement_value)} />
          </div>
        </SectionRow>

        <SectionRow title="Locations">
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters uk-margin-small-top">
            <div className="uk-overflow-auto">
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Location</th>
                    <th>Item Count</th>
                    <th>Inventory Value</th>
                    <th>Replacement Value</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.length === 0 && (
                    <tr><td colSpan={4}><div className="uk-text-center uk-padding-small uk-text-meta">No inventory value data found.</div></td></tr>
                  )}
                  {locations.map((row) => (
                    <tr key={row.location_id} onClick={() => setSelectedLocationId(String(row.location_id))} className="uk-link-reset">
                      <td className="semi-bold-font">{row.location_name}<div className="uk-text-meta">{row.location_code || "No code"}</div></td>
                      <td>{Number(row.item_count || 0).toLocaleString()}</td>
                      <td>{formatCurrency(row.inventory_value)}</td>
                      <td>{formatCurrency(row.replacement_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SectionRow>

        {selectedLocationId && (
          <SectionRow title={selectedLocation?.location_name || "Location"}>
            <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters uk-margin-small-top">
              <div className="uk-card-header uk-flex uk-flex-right uk-flex-middle">
                <button className="uk-button uk-button-default" type="button" onClick={() => setSelectedLocationId("")}>Close</button>
              </div>
              <div className="uk-overflow-auto">
                <table className="uk-table uk-table-hover uk-table-divider hms-table">
                  <thead><tr><th className="uk-table-expand">Item</th><th>Qty</th><th>Unit Cost</th><th>Replacement Cost</th><th>Inventory Value</th><th>Replacement Value</th></tr></thead>
                  <tbody>
                    {drillDownItems.map((item) => (
                      <tr key={item.item_id}>
                        <td>{item.item_name}<div className="uk-text-meta">{item.sku || "No SKU"}</div></td>
                        <td>{item.quantity ?? 0}</td>
                        <td>{item.unit_cost != null ? formatCurrency(item.unit_cost) : "-"}</td>
                        <td>{item.replacement_cost != null ? formatCurrency(item.replacement_cost) : "-"}</td>
                        <td>{formatCurrency(item.inventory_value)}</td>
                        <td>{formatCurrency(item.replacement_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionRow>
        )}
      </div>
    </section>
  );
}
